/**
 * AbandonedBelongingsQueuePage — supervisor / contractor_admin /
 * super_admin queue for cleaner-filed abandoned-belongings reports.
 *
 * Shows rows in 'reported' and 'acknowledged' status (the open queue).
 * Each row exposes the next legal action(s):
 *   - reported     → "Acknowledge"
 *   - acknowledged → "Mark removed" | "Mark billed"
 *
 * Mark-removed / mark-billed open an inline disposition-notes field
 * (optional, ≤1000 chars). Acknowledge fires immediately.
 *
 * Tenant-toggled by abandoned_belongings_tracking. Tenants whose
 * contract doesn't include the clause see a short explanation in
 * place of the queue. The role guard is at the router level.
 */

import { useState, type ReactNode } from 'react'
import { useTranslation }            from 'react-i18next'
import {
  useAbandonedBelongingsReports,
  OPEN_QUEUE_STATUSES,
  type AbandonedBelongingsReportRow,
}                                    from '@/hooks/useAbandonedBelongingsReports'
import {
  useDecideAbandonedBelongings,
  type AbandonedBelongingsDecision,
}                                    from '@/hooks/useDecideAbandonedBelongings'
import { useTenantFeatures }         from '@/hooks/useTenantFeatures'
import { isAppError }                from '@/lib/errors/AppError'

export function AbandonedBelongingsQueuePage(): ReactNode {
  const { t } = useTranslation()
  const { has: hasFeature, isLoading: flagsLoading } = useTenantFeatures()
  const featureEnabled = hasFeature('abandoned_belongings_tracking')
  const { data, isLoading, error } = useAbandonedBelongingsReports({ statuses: [...OPEN_QUEUE_STATUSES] })

  if (flagsLoading) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <p style={{ color: 'var(--brand-neutral-500)' }}>{t('abandonedBelongingsQueue.loading', 'Loading queue…')}</p>
      </section>
    )
  }
  if (!featureEnabled) {
    return (
      <section
        data-testid="abandoned-belongings-queue-disabled"
        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
      >
        <h1 style={titleStyle}>{t('abandonedBelongingsQueue.title', 'Abandoned belongings')}</h1>
        <p style={{ margin: 0, color: 'var(--brand-neutral-500)' }}>
          {t('abandonedBelongingsQueue.featureDisabled', 'Abandoned-belongings tracking is not enabled for this organization. Contact your admin to enable the workflow.')}
        </p>
      </section>
    )
  }

  return (
    <section
      data-testid="abandoned-belongings-queue"
      style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
    >
      <header>
        <h1 style={titleStyle}>{t('abandonedBelongingsQueue.title', 'Abandoned belongings')}</h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
          {t('abandonedBelongingsQueue.subtitle', 'Cleaner-filed reports awaiting your decision.')}
        </p>
      </header>

      {isLoading && (
        <p style={{ color: 'var(--brand-neutral-500)' }}>
          {t('abandonedBelongingsQueue.loading', 'Loading queue…')}
        </p>
      )}
      {error && (
        <p role="alert" data-testid="abandoned-belongings-queue-error" style={{ color: 'var(--brand-danger)' }}>
          {error instanceof Error ? error.message : t('abandonedBelongingsQueue.failedToLoad', 'Failed to load the queue.')}
        </p>
      )}

      {!isLoading && !error && (
        (data && data.length > 0)
          ? (
            <ul data-testid="abandoned-belongings-queue-list" style={listStyle}>
              {data.map((row) => (
                <QueueRow key={row.id} row={row} />
              ))}
            </ul>
          )
          : (
            <p data-testid="abandoned-belongings-queue-empty" style={{ color: 'var(--brand-neutral-500)' }}>
              {t('abandonedBelongingsQueue.empty', 'No abandoned-belongings reports waiting for a decision right now.')}
            </p>
          )
      )}
    </section>
  )
}

function QueueRow({ row }: { row: AbandonedBelongingsReportRow }): ReactNode {
  const { t } = useTranslation()
  const decide = useDecideAbandonedBelongings()
  const [pendingDecision, setPendingDecision] = useState<AbandonedBelongingsDecision | null>(null)
  const [notesDraft, setNotesDraft]           = useState('')

  async function submit(decision: AbandonedBelongingsDecision): Promise<void> {
    try {
      await decide.mutateAsync({
        reportId:         row.id,
        decision,
        dispositionNotes: decision === 'acknowledge' ? undefined : notesDraft,
      })
      setPendingDecision(null)
      setNotesDraft('')
    } catch { /* error rendered via decide.error */ }
  }

  const errMsg = decide.error && isAppError(decide.error) ? decide.error.userMessage : null
  const canAcknowledge = row.status === 'reported'
  const canTerminate   = row.status === 'acknowledged'

  return (
    <li
      data-testid="abandoned-belongings-queue-row"
      data-report-id={row.id}
      data-status={row.status}
      style={rowStyle}
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
        <StatusChip status={row.status} />
        <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.8rem' }}>
          {new Date(row.created_at).toLocaleString()}
        </span>
      </div>
      <p style={{ margin: 0, fontSize: '0.9rem' }}>{row.description}</p>

      {canAcknowledge && (
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <ActionButton
            testId="abandoned-belongings-acknowledge"
            label={t('abandonedBelongingsQueue.acknowledge', 'Acknowledge')}
            disabled={decide.isPending}
            onClick={() => { void submit('acknowledge') }}
          />
        </div>
      )}

      {canTerminate && pendingDecision === null && (
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <ActionButton
            testId="abandoned-belongings-mark-removed-open"
            label={t('abandonedBelongingsQueue.markRemoved', 'Mark removed')}
            disabled={decide.isPending}
            onClick={() => { setPendingDecision('mark_removed') }}
          />
          <ActionButton
            testId="abandoned-belongings-mark-billed-open"
            label={t('abandonedBelongingsQueue.markBilled', 'Mark billed')}
            disabled={decide.isPending}
            onClick={() => { setPendingDecision('mark_billed') }}
          />
        </div>
      )}

      {canTerminate && pendingDecision !== null && (
        <div data-testid="abandoned-belongings-disposition-form" style={formStyle}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
              {t('abandonedBelongingsQueue.notesLabel', 'Disposition notes (optional, ≤1000 chars)')}
            </span>
            <textarea
              data-testid="abandoned-belongings-disposition-input"
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value.slice(0, 1000))}
              rows={2}
              maxLength={1000}
              style={{
                fontSize:     '0.9rem',
                padding:      '0.4rem',
                borderRadius: 'var(--brand-radius-sm)',
                border:       '1px solid var(--brand-neutral-300)',
                resize:       'vertical',
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <ActionButton
              testId="abandoned-belongings-disposition-submit"
              label={
                pendingDecision === 'mark_removed'
                  ? t('abandonedBelongingsQueue.confirmRemoved', 'Confirm removed')
                  : t('abandonedBelongingsQueue.confirmBilled',  'Confirm billed')
              }
              disabled={decide.isPending}
              onClick={() => { void submit(pendingDecision) }}
            />
            <ActionButton
              testId="abandoned-belongings-disposition-cancel"
              label={t('abandonedBelongingsQueue.cancel', 'Cancel')}
              disabled={decide.isPending}
              variant="secondary"
              onClick={() => { setPendingDecision(null); setNotesDraft('') }}
            />
          </div>
        </div>
      )}

      {errMsg && (
        <p role="alert" data-testid="abandoned-belongings-decide-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
          {errMsg}
        </p>
      )}
    </li>
  )
}

function StatusChip({ status }: { status: AbandonedBelongingsReportRow['status'] }): ReactNode {
  return (
    <span
      data-testid="abandoned-belongings-queue-status-chip"
      data-status={status}
      style={{
        display:      'inline-block',
        padding:      '0.1rem 0.45rem',
        borderRadius: 'var(--brand-radius-full)',
        background:   'var(--brand-neutral-100)',
        color:        'var(--brand-neutral-700)',
        fontSize:     '0.7rem',
        fontWeight:   600,
      }}
    >
      {status}
    </span>
  )
}

function ActionButton(
  { testId, label, disabled, onClick, variant }: {
    testId: string; label: string; disabled: boolean; onClick: () => void; variant?: 'secondary'
  },
): ReactNode {
  const isSecondary = variant === 'secondary'
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding:      '0.4rem 0.8rem',
        background:   disabled
                        ? 'var(--brand-neutral-300)'
                        : isSecondary
                          ? 'transparent'
                          : 'var(--brand-primary)',
        color:        isSecondary ? 'var(--brand-neutral-700)' : 'var(--brand-white)',
        border:       isSecondary ? '1px solid var(--brand-neutral-300)' : 'none',
        borderRadius: 'var(--brand-radius-sm)',
        fontSize:     '0.85rem',
        fontWeight:   600,
        cursor:       disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  )
}

const titleStyle = { margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' } as const
const listStyle  = { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }
const rowStyle   = {
  background:    'var(--brand-white)',
  border:        '1px solid var(--brand-neutral-300)',
  borderRadius:  'var(--brand-radius-lg)',
  padding:       '0.75rem',
  display:       'flex',
  flexDirection: 'column' as const,
  gap:           '0.4rem',
}
const formStyle = {
  display:        'flex',
  flexDirection:  'column' as const,
  gap:            '0.5rem',
  padding:        '0.6rem',
  background:     'var(--brand-neutral-50, var(--brand-neutral-100))',
  borderRadius:   'var(--brand-radius-sm)',
}
